import { useMemo } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { PieChart, Pie, Cell } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Delta, KpiRow, resolveKpi } from './Kpi'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'

/*
 * One bounded figure on a half dial — a rate, a score, attainment of a target.
 *
 *     gauge: {
 *         value: <expression>,     e.g. { percentOf: ['paid', 'invoiced'] }
 *         format: 'percent',
 *         min: 0, max: 100,        any bounded scale; −100…100 for an NPS
 *         bands: [                 from `min` up, each to its `to`; the band the
 *             { to: 50,  color: 'danger',  label: '…' },   value is in names it
 *             { to: 80,  color: 'warning', label: '…' },
 *             { to: 100, color: 'success', label: '…' },
 *         ],
 *         target: 90,              a tick on the dial
 *         change: 'points', favorable: 'up'
 *     }
 *
 * A value outside the scale pins the needle to the end of the dial, but the
 * number printed is the real one — the dial is clamped, the figure never is.
 */

const Dial = styled.div`
    position: relative;
    flex: 1;
    min-height: 90px;
`

// The chart's own box, stretched over the dial. A layer of its own rather than
// a rule on every child div, which would pin the readout to the top as well.
const Layer = styled.div`
    position: absolute;
    inset: 0;
`

const Readout = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    line-height: 1.1;

    strong {
        ${widgetFont.hero}
        font-variant-numeric: tabular-nums;
    }
    .band { ${widgetFont.label} font-weight: 600; }
`

const Ends = styled.div`
    position: absolute;
    display: flex;
    justify-content: space-between;
    ${widgetFont.caption}
    color: ${p => p.theme.font.label};
    pointer-events: none;
`

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))

const GaugeWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format } = w
    const [ref, size] = useElementSize()
    const gauge = config.gauge ?? {}
    const min = gauge.min ?? 0
    const max = gauge.max ?? 100

    const kpi = useMemo(() => data.current
        ? resolveKpi({ id: 'gauge', value: gauge.value, format: gauge.format, change: gauge.change, favorable: gauge.favorable }, data, config.measures)
        : null, [data, gauge, config.measures])

    const bands = useMemo(() => {
        let from = min
        return (gauge.bands?.length ? gauge.bands : [{ to: max, color: 'primary' }]).map((b, i) => {
            const band = { ...b, from, to: Math.min(b.to, max), color: palette.color(b.color, i) }
            from = band.to
            return band
        }).filter(b => b.to > b.from)
    }, [gauge.bands, min, max, palette])

    const value = kpi?.current
    const band = value == null ? null : bands.find(b => value <= b.to) ?? bands[bands.length - 1]

    // The dial fills the box: as wide as it can be, and half as tall plus room for the ends
    const radius = Math.max(30, Math.min(size.width / 2 - 8, size.height - 34))
    const cx = size.width / 2
    const cy = Math.min(size.height - 26, radius + 8)
    const thickness = Math.max(10, radius * 0.2)

    const angleOf = (v) => 180 - ((clamp(v, min, max) - min) / (max - min || 1)) * 180
    const point = (angle, r) => [cx + r * Math.cos((angle * Math.PI) / 180), cy - r * Math.sin((angle * Math.PI) / 180)]

    // A pointer across the arc, its tip just outside it, so it can be found at a
    // glance on any band; it stops short of the middle, where the figure is
    const needle = value == null ? null : (() => {
        const a = angleOf(value)
        // At most 6px out: the dial keeps 8px of room above and beside it
        const tip = radius + Math.min(6, Math.max(4, thickness * 0.3))
        const base = radius - thickness - Math.max(12, radius * 0.2)
        const [tx, ty] = point(a, tip)
        const [bx, by] = point(a, base)
        const half = Math.max(5, radius * 0.075)
        const dx = half * Math.cos(((a + 90) * Math.PI) / 180)
        const dy = -half * Math.sin(((a + 90) * Math.PI) / 180)
        return `M ${bx + dx} ${by + dy} L ${tx} ${ty} L ${bx - dx} ${by - dy} Z`
    })()

    const target = gauge.target != null ? (() => {
        const a = angleOf(gauge.target)
        const [x1, y1] = point(a, radius + 3)
        const [x2, y2] = point(a, radius - thickness - 3)
        return { x1, y1, x2, y2 }
    })() : null

    // Each band split where the value falls: full colour up to it, faded past
    // it, so the dial reads as filled to the needle
    const reached = value == null ? max : clamp(value, min, max)
    const pieData = bands.flatMap(b => [
        { id: `${b.from}`, value: Math.max(0, Math.min(b.to, reached) - b.from), color: b.color, opacity: 0.95 },
        { id: `${b.from}-past`, value: Math.max(0, b.to - Math.max(b.from, reached)), color: b.color, opacity: 0.25 },
    ]).filter(d => d.value > 0)
    const ariaLabel = `${t(labels?.title)}: ${format(value, kpi?.format)}${band?.label ? `, ${t(band.label)}` : ''} (${format(min, kpi?.format)} – ${format(max, kpi?.format)})`

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <Dial role="img" aria-label={ariaLabel}>
                <Layer ref={ref}>
                    {size.width > 0 && size.height > 0 && (
                        <PieChart width={size.width} height={size.height}>
                            <Pie data={pieData} dataKey="value" cx={cx} cy={cy} startAngle={180} endAngle={0}
                                innerRadius={radius - thickness} outerRadius={radius} paddingAngle={bands.length > 1 ? 1 : 0}
                                stroke="none" isAnimationActive={false}>
                                {pieData.map(d => <Cell key={d.id} fill={d.color} fillOpacity={d.opacity} />)}
                            </Pie>
                            {target && <line {...target} stroke={palette.text} strokeWidth={2} strokeLinecap="round" />}
                            {needle && <path d={needle} fill={palette.text} stroke={palette.surface} strokeWidth={2.5}
                                paintOrder="stroke" strokeLinejoin="round" />}
                        </PieChart>
                    )}
                </Layer>
                {size.width > 0 && (
                    <>
                        <Readout style={{ bottom: size.height - cy }}>
                            <strong>{format(value, kpi?.format)}</strong>
                            {band?.label && <span className="band" style={{ color: band.color }}>{t(band.label)}</span>}
                            {kpi?.delta && <Delta delta={kpi.delta} kpiTone={kpi.tone} format={kpi.format} />}
                        </Readout>
                        <Ends style={{ top: cy + 6, left: cx - radius, width: radius * 2 }}>
                            <span>{format(min, kpi?.format)}</span>
                            <span>{format(max, kpi?.format)}</span>
                        </Ends>
                    </>
                )}
            </Dial>
        </AnalyticsCard>
    )
}

export default GaugeWidget
